import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInterval } from 'usehooks-ts';

const INTERVAL = 7500; // 7.5 seconds

const THIRTY_SECONDS = 30000; // 30 seconds
const ONE_MINUTE = 60000; // 1 minute

const NUMBER_OF_LABELS = 8; // Total number of labels to cycle through

/**
 * This hook provides a processing label that cycles through three states every 15 seconds.
 * It stops updating after two minutes.
 * The label is used to indicate that the OCR processing is ongoing.
 */
export const useProcessingLabel = ({ enabled }: { enabled: boolean }) => {
  const { t } = useTranslation();
  const [label, setLabel] = useState<string>(t('common:ocr.upload.processingLabel.batch1.0'));
  const numberOfIntervals = useRef(0);

  useInterval(
    () => {
      numberOfIntervals.current += 1;

      if (numberOfIntervals.current * INTERVAL >= ONE_MINUTE) {
        setLabel((prevLabel) => {
          const nextIndex = (parseInt(prevLabel.split('.').pop() || '0', 10) + 1) % NUMBER_OF_LABELS;
          return t(`common:ocr.upload.processingLabel.batch3.${nextIndex}`);
        });
        return;
      }

      if (numberOfIntervals.current * INTERVAL >= THIRTY_SECONDS) {
        setLabel((prevLabel) => {
          const nextIndex = (parseInt(prevLabel.split('.').pop() || '0', 10) + 1) % NUMBER_OF_LABELS;
          return t(`common:ocr.upload.processingLabel.batch2.${nextIndex}`);
        });
        return;
      }
    },
    enabled ? INTERVAL : null,
  );

  // Reset when disabled
  if (!enabled) {
    numberOfIntervals.current = 0;
    if (label !== t('common:ocr.upload.processingLabel.batch1.0')) {
      setLabel(t('common:ocr.upload.processingLabel.batch1.0'));
    }
  }

  return label;
};

/**
 * i18n
 *
 * Before 30 seconds
 * t('common:ocr.upload.processingLabel.batch1.0')
 *
 * After 30 seconds
 * t('common:ocr.upload.processingLabel.batch2.0')
 * t('common:ocr.upload.processingLabel.batch2.1')
 * t('common:ocr.upload.processingLabel.batch2.2')
 * t('common:ocr.upload.processingLabel.batch2.3')
 * t('common:ocr.upload.processingLabel.batch2.4')
 * t('common:ocr.upload.processingLabel.batch2.5')
 * t('common:ocr.upload.processingLabel.batch2.6')
 * t('common:ocr.upload.processingLabel.batch2.7')
 *
 * After 1 minute
 * t('common:ocr.upload.processingLabel.batch3.0')
 * t('common:ocr.upload.processingLabel.batch3.1')
 * t('common:ocr.upload.processingLabel.batch3.2')
 * t('common:ocr.upload.processingLabel.batch3.3')
 * t('common:ocr.upload.processingLabel.batch3.4')
 * t('common:ocr.upload.processingLabel.batch3.5')
 * t('common:ocr.upload.processingLabel.batch3.6')
 * t('common:ocr.upload.processingLabel.batch3.7')
 */
